/**
 * SecurityCards
 *
 * 3 cards de seguridad y control:
 *  - Palabras prohibidas (qué NO puede decir)
 *  - Ventana horaria (cuándo NO puede hablar)
 *  - Frecuencia por usuario (qué tan seguido puede contactar al mismo)
 *
 * Antes la frecuencia estaba en "Límites operacionales" — la movimos acá
 * porque conceptualmente es una restricción de seguridad (evita spam y
 * ban de la cuenta de WhatsApp). El resto de "Límites operacionales"
 * (presupuesto, ROI, equidad) se eliminó por no aplicar al modelo QR.
 */

import { useState } from "react";
import {
    ShieldAlert,
    Clock,
    ArrowRight,
    X,
    Send,
    Repeat,
} from "lucide-react";
import { Card } from "../../../shared/components/ui/Card";
import { AppliedInFooter } from "./RuleSliders";

interface SecurityCardsProps {
    forbiddenWords: string[];
    setForbiddenWords: (v: string[]) => void;
    startTime: string;
    setStartTime: (v: string) => void;
    endTime: string;
    setEndTime: (v: string) => void;
    frequencyCap: string;
    setFrequencyCap: (v: string) => void;
}

export default function SecurityCards({
    forbiddenWords,
    setForbiddenWords,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    frequencyCap,
    setFrequencyCap,
}: SecurityCardsProps) {
    const [newWord, setNewWord] = useState("");

    const handleAddWord = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && newWord.trim()) {
            setForbiddenWords([...forbiddenWords, newWord.trim()]);
            setNewWord("");
        }
    };

    const removeWord = (word: string) => {
        setForbiddenWords(forbiddenWords.filter((w) => w !== word));
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {/* Palabras Prohibidas */}
            <Card className="p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-50 rounded-lg text-red-500">
                        <ShieldAlert size={18} />
                    </div>
                    <div>
                        <h3 className="text-slate-900 font-bold text-base">
                            Palabras prohibidas
                        </h3>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">
                            Qué NO puede decir
                        </p>
                    </div>
                </div>

                <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap gap-1.5 min-h-[40px] max-h-20 overflow-y-auto content-start pr-1">
                        {forbiddenWords.length > 0 ? (
                            forbiddenWords.map((word, i) => (
                                <span
                                    key={i}
                                    className="px-2 py-1 rounded bg-red-50 border border-red-100 text-red-600 text-[10px] font-medium flex items-center gap-1"
                                >
                                    {word}
                                    <button
                                        onClick={() => removeWord(word)}
                                        className="text-red-400 hover:text-red-700 transition-colors"
                                    >
                                        <X size={10} />
                                    </button>
                                </span>
                            ))
                        ) : (
                            <span className="text-[10px] text-slate-400 italic">
                                Ninguna definida — agregá la primera abajo
                            </span>
                        )}
                    </div>
                    <input
                        type="text"
                        value={newWord}
                        onChange={(e) => setNewWord(e.target.value)}
                        onKeyDown={handleAddWord}
                        placeholder="+ Agregar palabra (Enter)"
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-900 text-xs focus:outline-none focus:border-red-200 focus:ring-1 focus:ring-red-100 transition-all"
                    />
                </div>

                <AppliedInFooter
                    items={[
                        {
                            icon: <ShieldAlert size={11} />,
                            label: "Bloquea mensajes antes de enviar",
                        },
                        {
                            icon: <Send size={11} />,
                            label: "Filtra contenido generado por IA",
                        },
                    ]}
                />
            </Card>

            {/* Ventana horaria */}
            <Card className="p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-500">
                        <Clock size={18} />
                    </div>
                    <div>
                        <h3 className="text-slate-900 font-bold text-base">
                            Ventana horaria
                        </h3>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">
                            Cuándo NO puede hablar
                        </p>
                    </div>
                </div>

                <div className="flex-1 flex items-center">
                    <div className="flex items-center justify-between gap-2 bg-slate-50 p-3 rounded-lg border border-slate-100 w-full">
                        <div className="flex flex-col">
                            <label className="text-[10px] text-slate-500 uppercase font-bold mb-0.5">
                                Inicio
                            </label>
                            <input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                className="bg-transparent text-slate-900 font-mono text-lg focus:outline-none w-24"
                            />
                        </div>
                        <div className="text-slate-400">
                            <ArrowRight size={14} />
                        </div>
                        <div className="flex flex-col text-right">
                            <label className="text-[10px] text-slate-500 uppercase font-bold mb-0.5">
                                Fin
                            </label>
                            <input
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                className="bg-transparent text-slate-900 font-mono text-lg focus:outline-none w-24 text-right"
                            />
                        </div>
                    </div>
                </div>

                <AppliedInFooter
                    items={[
                        {
                            icon: <Clock size={11} />,
                            label: "Pausa envíos fuera de horario",
                        },
                        {
                            icon: <Send size={11} />,
                            label: "Aplica a WhatsApp y notifications",
                        },
                    ]}
                />
            </Card>

            {/* Frecuencia por usuario */}
            <Card className="p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                        <Repeat size={18} />
                    </div>
                    <div>
                        <h3 className="text-slate-900 font-bold text-base">
                            Frecuencia por usuario
                        </h3>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">
                            Cuántas veces puede contactar
                        </p>
                    </div>
                </div>

                <div className="flex-1 flex items-center">
                    <select
                        value={frequencyCap}
                        onChange={(e) => setFrequencyCap(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-amber-300 transition-colors cursor-pointer"
                    >
                        <option value="1_month">Máx 1 mensaje / mes (muy conservador)</option>
                        <option value="2_week">Máx 2 mensajes / semana</option>
                        <option value="1_week">Máx 1 mensaje / semana</option>
                        <option value="3_days">Máx 1 / 3 días</option>
                        <option value="1_day">Máx 1 / día (alto riesgo)</option>
                    </select>
                </div>

                <AppliedInFooter
                    items={[
                        {
                            icon: <ShieldAlert size={11} />,
                            label: "Evita reportes de spam en WhatsApp",
                        },
                        {
                            icon: <Send size={11} />,
                            label: "Aplica a todas las campañas y envíos",
                        },
                    ]}
                />
            </Card>
        </div>
    );
}
